import * as React from "react"

// Sticky in-page section nav: a small slash-separated breadcrumb that
// smooth-scrolls to section anchors and highlights the active section via
// IntersectionObserver.
//
// The component is intentionally i18n-agnostic: callers pass already-translated
// labels via `sections`, so the same component serves the marketing site and
// the service dashboard. Linking is purely by DOM id - each `section.id` must
// match the `id` of an element rendered somewhere on the page (wrap the target
// in `<section id="..." className="scroll-mt-20">`).

export interface NavSection {
  id: string
  label: string
}

interface SectionNavProps {
  sections: NavSection[]
  /**
   * When true, clicking the first item scrolls to the very top of the page
   * (e.g. a hero) instead of to its section anchor. Defaults to false so each
   * item scrolls to its own section.
   */
  firstScrollsToTop?: boolean
  className?: string
}

const isDev = (() => {
  try {
    // Vite replaces this at build time in both consuming apps.
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV)
  } catch {
    return false
  }
})()

// Resolve an id to its currently-visible element. A page may render the same id
// in more than one layout branch (e.g. mobile + desktop), only one of which has
// a layout box at a time; offsetParent is null for display:none elements.
const getVisibleEl = (id: string): HTMLElement | null => {
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(`[id="${CSS.escape(id)}"]`)
  )
  return matches.find((el) => el.offsetParent !== null) ?? matches[0] ?? null
}

const SectionNav: React.FC<SectionNavProps> = ({
  sections,
  firstScrollsToTop = false,
  className = "",
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null)

  React.useEffect(() => {
    const handler = (entries: IntersectionObserverEntry[]) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
      if (visible) setActiveId(visible.target.id)
    }
    const io = new IntersectionObserver(handler, {
      rootMargin: "-30% 0px -60% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1],
    })
    // Observe every matching element across all layout branches; only the
    // visible one will ever intersect, so the active id stays correct.
    sections.forEach((s) => {
      const els = document.querySelectorAll<HTMLElement>(`[id="${CSS.escape(s.id)}"]`)
      if (els.length === 0 && isDev) {
        // Surface a dangling nav item immediately rather than failing silently
        // on click.
        console.warn(
          `[SectionNav] No element found for section id "${s.id}". ` +
            `Add an element with that id (e.g. <section id="${s.id}">) or remove the nav item.`
        )
      }
      els.forEach((el) => io.observe(el))
    })
    return () => io.disconnect()
  }, [sections])

  const onClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
    isFirst: boolean
  ) => {
    e.preventDefault()
    if (firstScrollsToTop && isFirst) {
      window.scrollTo({ top: 0, behavior: "smooth" })
      setActiveId(id)
      return
    }
    const target = getVisibleEl(id)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    setActiveId(id)
  }

  return (
    <nav
      aria-label="Page sections"
      className={`sticky top-0 z-30 -mx-4 sm:-mx-0 mb-6 bg-primary/95 backdrop-blur ${className}`}
    >
      <div className="flex items-center overflow-x-auto px-4 sm:px-0 py-2 text-xs scrollbar-thin">
        {sections.map((s, i) => {
          const isActive = activeId === s.id
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <span className="shrink-0 px-2 text-tertiary select-none">/</span>
              )}
              <a
                href={`#${s.id}`}
                onClick={(e) => onClick(e, s.id, i === 0)}
                className={`shrink-0 whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-accent font-medium"
                    : "text-secondary hover:text-primary"
                }`}
                aria-current={isActive ? "true" : undefined}
              >
                {s.label}
              </a>
            </React.Fragment>
          )
        })}
      </div>
    </nav>
  )
}

export default SectionNav
